import * as DialogPrimitive from '@radix-ui/react-dialog';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

interface DeployLiveConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export const DeployLiveConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: DeployLiveConfirmDialogProps) => {
  const { t } = useTranslation('sony_admin');

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        role="alertdialog"
        aria-modal="true"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <TriangleAlert className="size-4 inline bg-destructive-muted rounded-lg text-destructive p-2 box-content" />
              {t('sheet.deploy_dialog.title')}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogDescription>{t('sheet.deploy_dialog.description')}</AlertDialogDescription>

          <Separator className="my-7" />

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button size="md" variant="secondary" disabled={isLoading}>
                {t('sheet.deploy_dialog.cancel')}
              </Button>
            </AlertDialogCancel>

            <Button
              variant="danger"
              size="md"
              onClick={onConfirm}
              loading={isLoading}
              data-testid="sony-deploy-confirm-button"
            >
              {t('sheet.deploy_dialog.confirm')}
            </Button>
          </AlertDialogFooter>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
